import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FiFilter,
  FiRefreshCw,
  FiX,
  FiChevronDown,
  FiCheck,
  FiClock,
  FiSearch,
  FiArchive,
  FiMoreVertical,
  FiRepeat,
  FiShuffle,
} from "react-icons/fi";
import {
  fetchAllRuns,
  archiveRun,
  unarchiveRun,
  triggerVarietyCheck,
} from "../services/runService";
import { fetchGenerators } from "../services/generatorService";
import MarqueeText from "../components/common/MarqueeText";
import {
  fetchLatestSnapshot,
  flattenSnapshotConfig,
} from "../services/snapshotService";
import { getSubjectConfig } from "../utils/subjectConfig";
import {
  getCurriculumConfig,
  getRequestCategoryConfig,
  getModifierBadges,
} from "../utils/curriculumConfig";

// Status badge configuration
const getStatusConfig = (status) => {
  switch (status) {
    case "completed":
      return {
        label: "Completed",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        dotColor: "bg-green-500",
      };
    case "failed":
      return {
        label: "Failed",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        dotColor: "bg-red-500",
      };
    case "generating":
      return {
        label: "Generating",
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
        dotColor: "bg-blue-500",
        isPulsing: true,
      };
    case "evaluating":
      return {
        label: "Evaluating",
        bgColor: "bg-purple-100",
        textColor: "text-purple-800",
        dotColor: "bg-purple-500",
        isPulsing: true,
      };
    case "queued":
      return {
        label: "Queued",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-800",
        dotColor: "bg-yellow-500",
        isPulsing: true,
      };
    default:
      return {
        label: status || "Unknown",
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
        dotColor: "bg-gray-500",
      };
  }
};

// Format date/time concisely
const formatDateTime = (dateString) => {
  if (!dateString) {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Sort indicator component
const SortIndicator = ({ columnKey, sortConfig }) => {
  if (sortConfig.key !== columnKey) {
    return null;
  }
  return (
    <span className="ml-1 text-gray-500">
      {sortConfig.direction === "asc" ? "▲" : "▼"}
    </span>
  );
};

SortIndicator.propTypes = {
  columnKey: PropTypes.string.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(["asc", "desc"]).isRequired,
  }).isRequired,
};

// Column Filter Component - inline filter dropdown for table headers
const ColumnFilter = ({
  options,
  selectedValues,
  onChange,
  label,
  align = "left",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const hasFilters = selectedValues.length > 0;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`ml-1 rounded p-0.5 transition-colors ${
          hasFilters
            ? "text-primary-600 hover:bg-primary-100"
            : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        }`}
        title={`Filter by ${label}`}
      >
        <FiFilter className="size-3" />
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 mt-1 min-w-[160px] rounded-lg border border-gray-200 bg-white shadow-lg ${align === "right" ? "right-0" : "left-0"}`}
        >
          <div className="border-b border-gray-100 px-3 py-2">
            <span className="text-xs font-medium text-gray-500">
              Filter by {label}
            </span>
          </div>
          <div className="max-h-48 overflow-auto py-1">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">No options</div>
            ) : (
              options.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(option.value);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-50 ${
                      isSelected ? "bg-primary-50" : ""
                    }`}
                  >
                    <span
                      className={`flex size-3.5 items-center justify-center rounded border ${
                        isSelected
                          ? "border-primary-500 bg-primary-500"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <FiCheck className="size-2.5 text-white" />
                      )}
                    </span>
                    <span className="flex-1 truncate">{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
          {hasFilters && (
            <div className="border-t border-gray-100 px-3 py-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ColumnFilter.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  align: PropTypes.oneOf(["left", "right"]),
};

// Custom Multi-Select Dropdown Component
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  renderOption,
  width = "w-40",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex ${width} items-center justify-between rounded-lg border-2 border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200`}
      >
        <span className="truncate text-gray-500">
          {selectedValues.length > 0
            ? `${selectedValues.length} selected`
            : placeholder}
        </span>
        <FiChevronDown
          className={`ml-2 size-4 shrink-0 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute inset-x-0 z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border-2 border-gray-300 bg-white shadow-lg">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No options available
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                    isSelected ? "bg-primary-50" : ""
                  }`}
                >
                  <span className="flex-1">
                    {renderOption ? renderOption(option) : option.label}
                  </span>
                  {isSelected && (
                    <FiCheck className="ml-2 size-4 shrink-0 text-primary-600" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  renderOption: PropTypes.func,
  width: PropTypes.string,
};

const PastRuns = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [generators, setGenerators] = useState([]);
  const [snapshotItems, setSnapshotItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessfulOnly, setShowSuccessfulOnly] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [showArchived, setShowArchived] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default 20 runs per page

  // Filter states
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGenerators, setSelectedGenerators] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCurricula, setSelectedCurricula] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDatasetVersions, setSelectedDatasetVersions] = useState([]);
  const [selectedEvaluatorVersions, setSelectedEvaluatorVersions] = useState(
    []
  );
  const [runIdSearch, setRunIdSearch] = useState("");
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncedAgoText, setSyncedAgoText] = useState("");

  // Menu state
  const [openMenuRunId, setOpenMenuRunId] = useState(null);
  const menuRef = useRef(null);

  const snapshotCache = useRef(null);

  const loadData = async () => {
    setLoading(true);

    const snapshotPromise = snapshotCache.current
      ? Promise.resolve({ data: snapshotCache.current, error: null })
      : fetchLatestSnapshot();

    const [runsResult, generatorsResult, snapshotResult] = await Promise.all([
      fetchAllRuns({ limit: 10000, includeArchived: true }),
      fetchGenerators(),
      snapshotPromise,
    ]);

    if (runsResult.error) {
      console.error("Error loading runs:", runsResult.error);
    } else {
      setRuns(runsResult.data || []);
      setLastSyncTime(new Date());
    }

    if (generatorsResult.error) {
      console.error("Error loading generators:", generatorsResult.error);
    } else {
      setGenerators(generatorsResult.data || []);
    }

    if (snapshotResult.error) {
      console.error("Error loading snapshot:", snapshotResult.error);
    } else {
      if (!snapshotCache.current) {
        snapshotCache.current = flattenSnapshotConfig(snapshotResult.data);
      }
      setSnapshotItems(snapshotCache.current);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only load once on mount

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest('button[title="More actions"]')
      ) {
        setOpenMenuRunId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update "synced ago" text every 10 seconds
  useEffect(() => {
    const updateSyncedAgo = () => {
      if (!lastSyncTime) {
        setSyncedAgoText("");
        return;
      }
      const now = new Date();
      const diffMs = now - lastSyncTime;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);

      if (diffSec < 10) {
        setSyncedAgoText("Synced just now");
      } else if (diffSec < 60) {
        setSyncedAgoText(`Synced ${diffSec}s ago`);
      } else if (diffMin < 60) {
        setSyncedAgoText(`Synced ${diffMin}m ago`);
      } else {
        setSyncedAgoText(`Synced ${diffHour}h ago`);
      }
    };

    updateSyncedAgo();
    const interval = setInterval(updateSyncedAgo, 10000);
    return () => clearInterval(interval);
  }, [lastSyncTime]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const { data, error } = await fetchAllRuns({
      limit: 10000,
      includeArchived: true,
    });

    if (error) {
      console.error("Error refreshing runs:", error);
    } else {
      setRuns(data || []);
      setLastSyncTime(new Date());
    }

    setIsRefreshing(false);
  };

  const handleArchiveToggle = async (runId, isCurrentlyArchived, event) => {
    // Stop event propagation to prevent navigation to run detail
    if (event) {
      event.stopPropagation();
    }

    // Close menu
    setOpenMenuRunId(null);

    try {
      if (isCurrentlyArchived) {
        const { error } = await unarchiveRun(runId);
        if (error) {
          console.error("Error unarchiving run:", error);
          alert("Failed to unarchive run");
          return;
        }
      } else {
        const { error } = await archiveRun(runId);
        if (error) {
          console.error("Error archiving run:", error);
          alert("Failed to archive run");
          return;
        }
      }

      // Update local state
      setRuns((prevRuns) =>
        prevRuns.map((run) =>
          run.id === runId
            ? {
                ...run,
                is_archived: !isCurrentlyArchived,
                archived_at: !isCurrentlyArchived
                  ? new Date().toISOString()
                  : null,
              }
            : run
        )
      );
    } catch (error) {
      console.error("Error toggling archive status:", error);
      alert("Failed to update archive status");
    }
  };

  const handleMenuToggle = (runId, event) => {
    event.stopPropagation();
    setOpenMenuRunId(openMenuRunId === runId ? null : runId);
  };

  const availableGrades = useMemo(() => {
    return [...new Set(snapshotItems.map((item) => item.grade))].sort(
      (a, b) => {
        if (a === "K") {
          return -1;
        }
        if (b === "K") {
          return 1;
        }
        return parseInt(a) - parseInt(b);
      }
    );
  }, [snapshotItems]);

  const availableSubjects = useMemo(() => {
    return [...new Set(snapshotItems.map((item) => item.subject))].sort();
  }, [snapshotItems]);

  // Extract unique values from runs for filters
  const availableStatuses = useMemo(() => {
    return [...new Set(runs.map((run) => run.status).filter(Boolean))].sort();
  }, [runs]);

  const availableCurricula = useMemo(() => {
    return [
      ...new Set(runs.map((run) => run.filters?.curriculum).filter(Boolean)),
    ].sort();
  }, [runs]);

  const availableCategories = useMemo(() => {
    return [
      ...new Set(
        runs.map((run) => run.filters?.request_category).filter(Boolean)
      ),
    ].sort();
  }, [runs]);

  const availableDatasetVersions = useMemo(() => {
    return [
      ...new Set(runs.map((run) => run.dataset_version).filter(Boolean)),
    ].sort((a, b) => b.localeCompare(a)); // Sort descending (newest first)
  }, [runs]);

  const availableEvaluatorVersions = useMemo(() => {
    return [
      ...new Set(runs.map((run) => run.inceptbench_version).filter(Boolean)),
    ].sort((a, b) => b.localeCompare(a)); // Sort descending (newest first)
  }, [runs]);

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedGrades([]);
    setSelectedSubjects([]);
    setSelectedGenerators([]);
    setSelectedStatuses([]);
    setSelectedCurricula([]);
    setSelectedCategories([]);
    setSelectedDatasetVersions([]);
    setSelectedEvaluatorVersions([]);
    setRunIdSearch("");
    setCurrentPage(1); // Reset to first page
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedGrades,
    selectedSubjects,
    selectedGenerators,
    selectedStatuses,
    selectedCurricula,
    selectedCategories,
    selectedDatasetVersions,
    selectedEvaluatorVersions,
    showSuccessfulOnly,
    runIdSearch,
  ]);

  // Check if any filters are active (excluding successful runs toggle)
  const hasActiveFilters =
    selectedGrades.length > 0 ||
    selectedSubjects.length > 0 ||
    selectedGenerators.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedCurricula.length > 0 ||
    selectedCategories.length > 0 ||
    selectedDatasetVersions.length > 0 ||
    selectedEvaluatorVersions.length > 0 ||
    runIdSearch.trim().length > 0;

  // Filter runs
  const filteredRuns = runs.filter((run) => {
    // Filter by archive status first
    if (showArchived && !run.is_archived) {
      return false;
    }
    if (!showArchived && run.is_archived) {
      return false;
    }

    // Filter by run ID search
    if (runIdSearch.trim()) {
      const searchLower = runIdSearch.trim().toLowerCase();
      if (!run.id.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Filter by successful runs toggle
    if (showSuccessfulOnly && run.status !== "completed") {
      return false;
    }

    // Filter by status
    if (selectedStatuses.length > 0) {
      if (!selectedStatuses.includes(run.status)) {
        return false;
      }
    }

    // Filter by grades
    if (selectedGrades.length > 0) {
      const runGrades = run.filters?.grades || [];
      const hasMatchingGrade = runGrades.some((grade) =>
        selectedGrades.includes(grade)
      );
      if (!hasMatchingGrade) {
        return false;
      }
    }

    // Filter by subjects
    if (selectedSubjects.length > 0) {
      const selectedLower = selectedSubjects.map((s) => s.toLowerCase());
      const runSubjects = run.filters?.subjects || [];
      const hasMatchingSubject = runSubjects.some((subject) =>
        selectedLower.includes(subject.toLowerCase())
      );
      if (!hasMatchingSubject) {
        return false;
      }
    }

    // Filter by generators
    if (selectedGenerators.length > 0) {
      if (!selectedGenerators.includes(run.generator_id)) {
        return false;
      }
    }

    // Filter by curriculum
    if (selectedCurricula.length > 0) {
      if (!selectedCurricula.includes(run.filters?.curriculum)) {
        return false;
      }
    }

    // Filter by request category
    if (selectedCategories.length > 0) {
      if (!selectedCategories.includes(run.filters?.request_category)) {
        return false;
      }
    }

    // Filter by dataset version
    if (selectedDatasetVersions.length > 0) {
      if (!selectedDatasetVersions.includes(run.dataset_version)) {
        return false;
      }
    }

    // Filter by evaluator (inceptbench) version
    if (selectedEvaluatorVersions.length > 0) {
      if (!selectedEvaluatorVersions.includes(run.inceptbench_version)) {
        return false;
      }
    }

    return true;
  });

  // Sort runs
  const sortedRuns = [...filteredRuns].sort((a, b) => {
    let aValue, bValue;

    switch (sortConfig.key) {
      case "generator":
        aValue = a.bm_generators?.name || "";
        bValue = b.bm_generators?.name || "";
        break;
      case "status":
        aValue = a.status || "";
        bValue = b.status || "";
        break;
      case "curriculum":
        aValue = a.filters?.curriculum || "";
        bValue = b.filters?.curriculum || "";
        break;
      case "grade": {
        // Handle grade sorting (K comes before numbers, then numeric order)
        const aGrade = a.filters?.grades?.[0] ?? "";
        const bGrade = b.filters?.grades?.[0] ?? "";
        aValue = aGrade === "K" ? -1 : parseInt(aGrade) || 0;
        bValue = bGrade === "K" ? -1 : parseInt(bGrade) || 0;
        break;
      }
      case "subject":
        aValue = a.filters?.subjects?.[0] || "";
        bValue = b.filters?.subjects?.[0] || "";
        break;
      case "aggregate_score":
        aValue = parseFloat(a.aggregate_score) || 0;
        bValue = parseFloat(b.aggregate_score) || 0;
        break;
      case "pass_rate":
        aValue = parseFloat(a.pass_rate) || 0;
        bValue = parseFloat(b.pass_rate) || 0;
        break;
      case "created_at":
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      case "dataset_version":
        aValue = parseInt(a.dataset_version) || 0;
        bValue = parseInt(b.dataset_version) || 0;
        break;
      case "inceptbench_version":
        aValue = a.inceptbench_version || "";
        bValue = b.inceptbench_version || "";
        break;
      case "curriculum_version":
        aValue = a.curriculum_version || "";
        bValue = b.curriculum_version || "";
        break;
      default:
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
    }

    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Client-side pagination of filtered and sorted runs
  const totalFilteredCount = sortedRuns.length;
  const totalPages = Math.ceil(totalFilteredCount / pageSize);
  const paginatedRuns = sortedRuns.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-10">
            <div className="mb-2 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary-100">
                <FiClock className="size-5 text-primary-600" />
              </div>
              <h1 className="text-4xl font-semibold text-gray-900">
                Past Runs
              </h1>
            </div>
            <p className="mt-3 text-lg text-gray-600">
              View your benchmark run history and results.
            </p>
          </div>

          {/* Loading Spinner */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-y-2 border-primary-600"></div>
                <p className="text-sm text-gray-500">Loading runs...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 flex items-center justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary-100">
                <FiClock className="size-5 text-primary-600" />
              </div>
              <h1 className="text-4xl font-semibold text-gray-900">
                {showArchived ? "Archived Runs" : "Past Runs"}
              </h1>
            </div>
            <p className="mt-3 text-lg text-gray-600">
              {showArchived
                ? "View your archived benchmark runs."
                : "View your benchmark run history and results."}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700"
            >
              <FiArchive className="size-5" />
              <span>{showArchived ? "Show Active" : "Show Archived"}</span>
            </button>
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiRefreshCw
                  className={`size-5 ${isRefreshing ? "animate-spin" : ""}`}
                />
                <span>Refresh</span>
              </button>
              {syncedAgoText && (
                <span className="text-xs text-gray-400">{syncedAgoText}</span>
              )}
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="relative z-20 mb-6 rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50/50 shadow-sm">
          {/* Filter Controls Bar */}
          <div
            className={`overflow-visible ${hasActiveFilters ? "rounded-t-2xl" : "rounded-2xl"} border-b border-gray-100 bg-white/80 px-6 py-4 backdrop-blur-sm`}
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Left: Info */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>
                    Use{" "}
                    <FiFilter className="mx-0.5 inline size-3 text-gray-400" />{" "}
                    in column headers to filter, click on column header to sort{" "}
                    <span className="mx-0.5 inline text-gray-400">▲▼</span>
                  </span>
                </div>
              </div>

              {/* Right: Search and Actions */}
              <div className="flex items-center gap-3">
                {/* Run ID Search */}
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={runIdSearch}
                    onChange={(e) => setRunIdSearch(e.target.value)}
                    placeholder="Search by Run ID..."
                    className="w-64 rounded-lg border-2 border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 transition-colors placeholder:text-gray-400 hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                  {runIdSearch && (
                    <button
                      onClick={() => setRunIdSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
                    >
                      <FiX className="size-4" />
                    </button>
                  )}
                </div>

                {/* Successful Runs Toggle */}
                <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 transition-colors hover:bg-gray-100">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={showSuccessfulOnly}
                      onChange={(e) => setShowSuccessfulOnly(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-green-500"></div>
                    <div className="absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4"></div>
                  </div>
                  <span className="text-xs font-medium text-gray-700">
                    Completed
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="rounded-b-2xl border-b border-gray-100 bg-gray-50/50 px-6 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">
                    Active filters:
                  </span>
                  {runIdSearch.trim() && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                      <FiSearch className="size-3" />
                      ID:{" "}
                      {runIdSearch.trim().length > 12
                        ? `${runIdSearch.trim().slice(0, 12)}...`
                        : runIdSearch.trim()}
                      <button
                        onClick={() => setRunIdSearch("")}
                        className="hover:text-gray-900"
                      >
                        <FiX className="size-3" />
                      </button>
                    </span>
                  )}
                  {selectedGrades.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedGrades.map((grade) => (
                        <span
                          key={grade}
                          className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700"
                        >
                          Grade {grade === "K" ? "K" : grade}
                          <button
                            onClick={() =>
                              setSelectedGrades(
                                selectedGrades.filter((g) => g !== grade)
                              )
                            }
                            className="hover:text-blue-900"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {selectedSubjects.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedSubjects.map((subject) => {
                        const subjectConfig = getSubjectConfig(subject);
                        return (
                          <span
                            key={subject}
                            className={`inline-flex items-center gap-1 rounded-md ${subjectConfig.bgColor} px-2 py-1 text-xs font-medium ${subjectConfig.color}`}
                          >
                            {subjectConfig.icon && (
                              <subjectConfig.icon className="size-3" />
                            )}
                            {subjectConfig.name}
                            <button
                              onClick={() =>
                                setSelectedSubjects(
                                  selectedSubjects.filter((s) => s !== subject)
                                )
                              }
                              className="hover:opacity-70"
                            >
                              <FiX className="size-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {selectedGenerators.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedGenerators.map((genId) => {
                        const generator = generators.find(
                          (g) => g.id === genId
                        );
                        return (
                          <span
                            key={genId}
                            className="inline-flex items-center gap-1 rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700"
                          >
                            {generator?.name || "Unknown"}
                            <button
                              onClick={() =>
                                setSelectedGenerators(
                                  selectedGenerators.filter((g) => g !== genId)
                                )
                              }
                              className="hover:text-green-900"
                            >
                              <FiX className="size-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {selectedStatuses.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedStatuses.map((status) => (
                        <span
                          key={status}
                          className="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700"
                        >
                          {status}
                          <button
                            onClick={() =>
                              setSelectedStatuses(
                                selectedStatuses.filter((s) => s !== status)
                              )
                            }
                            className="hover:text-purple-900"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {selectedCurricula.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedCurricula.map((curr) => {
                        const currConfig = getCurriculumConfig(curr);
                        return (
                          <span
                            key={curr}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${currConfig.bgColor} ${currConfig.color}`}
                          >
                            {currConfig.abbreviation}
                            <button
                              onClick={() =>
                                setSelectedCurricula(
                                  selectedCurricula.filter((c) => c !== curr)
                                )
                              }
                              className="opacity-70 hover:opacity-100"
                            >
                              <FiX className="size-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {selectedCategories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedCategories.map((cat) => {
                        const catConfig = getRequestCategoryConfig(cat);
                        return (
                          <span
                            key={cat}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${catConfig.bgColor} ${catConfig.color}`}
                          >
                            {catConfig.label}
                            <button
                              onClick={() =>
                                setSelectedCategories(
                                  selectedCategories.filter((c) => c !== cat)
                                )
                              }
                              className="opacity-70 hover:opacity-100"
                            >
                              <FiX className="size-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {selectedDatasetVersions.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedDatasetVersions.map((ver) => (
                        <span
                          key={ver}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700"
                        >
                          Dataset v{ver}
                          <button
                            onClick={() =>
                              setSelectedDatasetVersions(
                                selectedDatasetVersions.filter((v) => v !== ver)
                              )
                            }
                            className="hover:text-emerald-900"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {selectedEvaluatorVersions.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedEvaluatorVersions.map((ver) => (
                        <span
                          key={ver}
                          className="inline-flex items-center gap-1 rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700"
                        >
                          Evaluator v{ver}
                          <button
                            onClick={() =>
                              setSelectedEvaluatorVersions(
                                selectedEvaluatorVersions.filter(
                                  (v) => v !== ver
                                )
                              )
                            }
                            className="hover:text-indigo-900"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Clear All Button */}
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition-all hover:bg-red-100 hover:shadow-sm"
                >
                  <FiX className="size-3.5" />
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Runs Table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            {paginatedRuns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-gray-500">
                  {hasActiveFilters || showSuccessfulOnly
                    ? "No runs match your filters."
                    : "No completed runs yet. Start a benchmark run to see results here."}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="bg-gray-50 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("generator")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("generator");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Generator
                        <SortIndicator
                          columnKey="generator"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={generators.map((g) => ({
                          value: g.id,
                          label: g.name,
                        }))}
                        selectedValues={selectedGenerators}
                        onChange={setSelectedGenerators}
                        label="Generator"
                      />
                    </th>
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("status")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("status");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Status
                        <SortIndicator
                          columnKey="status"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={availableStatuses.map((s) => ({
                          value: s,
                          label: s.charAt(0).toUpperCase() + s.slice(1),
                        }))}
                        selectedValues={selectedStatuses}
                        onChange={setSelectedStatuses}
                        label="Status"
                      />
                    </th>
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("curriculum")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("curriculum");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Curr.
                        <SortIndicator
                          columnKey="curriculum"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={availableCurricula.map((c) => ({
                          value: c,
                          label: getCurriculumConfig(c).abbreviation,
                        }))}
                        selectedValues={selectedCurricula}
                        onChange={setSelectedCurricula}
                        label="Curriculum"
                      />
                    </th>
                    {availableCategories.length > 1 && (
                      <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <span className="cursor-default">Cat.</span>
                        <ColumnFilter
                          options={availableCategories.map((c) => ({
                            value: c,
                            label: getRequestCategoryConfig(c).label,
                          }))}
                          selectedValues={selectedCategories}
                          onChange={setSelectedCategories}
                          label="Category"
                        />
                      </th>
                    )}
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("grade")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("grade");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Grade
                        <SortIndicator
                          columnKey="grade"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={availableGrades.map((g) => ({
                          value: g,
                          label: g === "K" ? "K" : `Grade ${g}`,
                        }))}
                        selectedValues={selectedGrades}
                        onChange={setSelectedGrades}
                        label="Grade"
                      />
                    </th>
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("subject")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("subject");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Subject
                        <SortIndicator
                          columnKey="subject"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={availableSubjects.map((s) => ({
                          value: s,
                          label:
                            s.charAt(0).toUpperCase() +
                            s.slice(1).toLowerCase(),
                        }))}
                        selectedValues={selectedSubjects}
                        onChange={setSelectedSubjects}
                        label="Subject"
                      />
                    </th>
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("aggregate_score")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("aggregate_score");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Score
                        <SortIndicator
                          columnKey="aggregate_score"
                          sortConfig={sortConfig}
                        />
                      </span>
                    </th>
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("pass_rate")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("pass_rate");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Pass
                        <SortIndicator
                          columnKey="pass_rate"
                          sortConfig={sortConfig}
                        />
                      </span>
                    </th>
                    <th className="p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("created_at")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("created_at");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Created
                        <SortIndicator
                          columnKey="created_at"
                          sortConfig={sortConfig}
                        />
                      </span>
                    </th>
                    <th className="p-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("dataset_version")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("dataset_version");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Dataset
                        <SortIndicator
                          columnKey="dataset_version"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={availableDatasetVersions.map((v) => ({
                          value: v,
                          label: `v${v}`,
                        }))}
                        selectedValues={selectedDatasetVersions}
                        onChange={setSelectedDatasetVersions}
                        label="Dataset Version"
                        align="right"
                      />
                    </th>
                    <th className="p-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("inceptbench_version")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("inceptbench_version");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Evaluator
                        <SortIndicator
                          columnKey="inceptbench_version"
                          sortConfig={sortConfig}
                        />
                      </span>
                      <ColumnFilter
                        options={availableEvaluatorVersions.map((v) => ({
                          value: v,
                          label: `v${v}`,
                        }))}
                        selectedValues={selectedEvaluatorVersions}
                        onChange={setSelectedEvaluatorVersions}
                        label="Evaluator Version"
                        align="right"
                      />
                    </th>
                    <th className="p-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      <span
                        role="button"
                        tabIndex="0"
                        onClick={() => handleSort("curriculum_version")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSort("curriculum_version");
                          }
                        }}
                        className="cursor-pointer hover:text-gray-700"
                      >
                        Curriculum Version
                        <SortIndicator
                          columnKey="curriculum_version"
                          sortConfig={sortConfig}
                        />
                      </span>
                    </th>
                    <th className="w-8 px-1 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedRuns.map((run) => {
                    const statusConfig = getStatusConfig(run.status);

                    return (
                      <tr
                        key={run.id}
                        onClick={() => {
                          navigate(`/past-runs/${run.id}`);
                        }}
                        title="Click to view run details"
                        className="group cursor-pointer transition-colors duration-150 hover:bg-primary-50"
                      >
                        <td className="w-[200px] bg-white p-3 transition-colors group-hover:bg-primary-50">
                          <MarqueeText
                            text={run.bm_generators?.name || "[Deleted]"}
                            maxWidth={180}
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
                            >
                              <span
                                className={`size-1.5 rounded-full ${statusConfig.dotColor} ${
                                  statusConfig.isPulsing ? "animate-pulse" : ""
                                }`}
                              />
                              {statusConfig.label}
                            </span>
                            {run.is_static === true && (
                              <span
                                title="Static content detected"
                                className="inline-flex items-center rounded-full bg-red-100 p-1 text-red-600"
                              >
                                <FiRepeat className="size-3" />
                              </span>
                            )}
                            {run.variety_status === "running" && (
                              <span
                                title="Checking variety..."
                                className="inline-flex items-center rounded-full bg-blue-100 p-1 text-blue-600"
                              >
                                <FiShuffle className="size-3 animate-spin" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm">
                          {run.filters?.curriculum ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getCurriculumConfig(run.filters.curriculum).bgColor} ${getCurriculumConfig(run.filters.curriculum).color}`}
                            >
                              {
                                getCurriculumConfig(run.filters.curriculum)
                                  .abbreviation
                              }
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        {availableCategories.length > 1 && (
                          <td className="whitespace-nowrap p-3 text-sm">
                            <div className="flex items-center gap-1">
                              {run.filters?.request_category ? (
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRequestCategoryConfig(run.filters.request_category).bgColor} ${getRequestCategoryConfig(run.filters.request_category).color}`}
                                >
                                  {
                                    getRequestCategoryConfig(
                                      run.filters.request_category
                                    ).shortLabel
                                  }
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                              {getModifierBadges(run.filters).map((badge) => (
                                <span
                                  key={badge.key}
                                  title={badge.label}
                                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${badge.bgColor} ${badge.color}`}
                                >
                                  {badge.shortLabel}
                                </span>
                              ))}
                            </div>
                          </td>
                        )}
                        <td className="p-3 text-sm text-gray-500">
                          {run.filters?.grades &&
                          Array.isArray(run.filters.grades) &&
                          run.filters.grades.length > 0 ? (
                            <div className="flex flex-wrap gap-0.5">
                              {run.filters.grades.map((grade, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex max-w-[80px] items-center rounded-full bg-blue-100 px-2 py-0.5"
                                >
                                  <MarqueeText
                                    text={grade}
                                    maxWidth={64}
                                    textClassName="text-xs font-semibold text-blue-700"
                                  />
                                </span>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-500">
                          {run.filters?.subjects &&
                          Array.isArray(run.filters.subjects) &&
                          run.filters.subjects.length > 0 ? (
                            <div className="flex flex-wrap gap-0.5">
                              {run.filters.subjects.map((subject, idx) => {
                                const subjectConfig = getSubjectConfig(subject);
                                return (
                                  <span
                                    key={idx}
                                    className={`inline-flex max-w-[80px] items-center rounded-full ${subjectConfig.bgColor} px-1.5 py-0.5`}
                                  >
                                    <MarqueeText
                                      text={
                                        subject.charAt(0).toUpperCase() +
                                        subject.slice(1).toLowerCase()
                                      }
                                      maxWidth={64}
                                      textClassName={`text-xs font-medium ${subjectConfig.color}`}
                                    />
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-gray-900">
                          {run.aggregate_score
                            ? `${(parseFloat(run.aggregate_score) * 100).toFixed(2)}%`
                            : "-"}
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-gray-900">
                          {run.pass_rate
                            ? `${(parseFloat(run.pass_rate) * 100).toFixed(2)}%`
                            : "-"}
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm">
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                            {formatDateTime(run.created_at)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap p-3 text-center text-sm">
                          {run.dataset_version ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                              v{run.dataset_version}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap p-3 text-center text-sm">
                          {run.inceptbench_version ? (
                            <span className="relative inline-flex items-center">
                              <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                v{run.inceptbench_version}
                              </span>
                              {run.inceptbench_version === "2.3.5" && (
                                <span className="absolute -right-1 -top-2 rounded-sm bg-amber-400 px-1 py-px text-[9px] font-bold leading-none tracking-wide text-white">
                                  EXP
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap p-3 text-center text-sm">
                          {run.curriculum_version ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                              v{run.curriculum_version}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td
                          className="relative whitespace-nowrap px-1 py-3 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => handleMenuToggle(run.id, e)}
                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            title="More actions"
                          >
                            <FiMoreVertical className="size-4" />
                          </button>

                          {/* Dropdown Menu */}
                          {openMenuRunId === run.id && (
                            <div
                              ref={menuRef}
                              className="absolute right-full top-2 z-50 mr-0 w-36 rounded-lg border border-gray-200 bg-white shadow-lg"
                            >
                              <button
                                onClick={(e) =>
                                  handleArchiveToggle(
                                    run.id,
                                    run.is_archived,
                                    e
                                  )
                                }
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                              >
                                <FiArchive className="size-4" />
                                <span>
                                  {run.is_archived ? "Unarchive" : "Archive"}
                                </span>
                              </button>
                              {run.status === "completed" &&
                                (!run.variety_status ||
                                  run.variety_status === "failed" ||
                                  (run.variety_status === "completed" &&
                                    run.variety_score === null)) && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setOpenMenuRunId(null);
                                      const result = await triggerVarietyCheck(
                                        run.id
                                      );
                                      if (result.success) {
                                        setRuns((prev) =>
                                          prev.map((r) =>
                                            r.id === run.id
                                              ? {
                                                  ...r,
                                                  variety_status: "running",
                                                }
                                              : r
                                          )
                                        );
                                      } else {
                                        // Trigger failed (e.g., already running/completed), refresh to show actual state
                                        handleRefresh();
                                      }
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                                  >
                                    <FiShuffle className="size-4" />
                                    <span>Variety Check</span>
                                  </button>
                                )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Controls */}
          {totalFilteredCount > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
              {/* Left: Page Size Selector */}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>per page</span>
              </div>

              {/* Center: Showing X to Y of Z */}
              <div className="text-sm text-gray-600">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalFilteredCount)}
                </span>{" "}
                of <span className="font-medium">{totalFilteredCount}</span>{" "}
                runs
              </div>

              {/* Right: Navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="flex size-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    );
                  })
                  .map((page, idx, arr) => {
                    // Add ellipsis between non-consecutive pages
                    const prevPage = arr[idx - 1];
                    const showEllipsis = prevPage && page - prevPage > 1;

                    return (
                      <div key={page} className="flex items-center gap-1">
                        {showEllipsis && (
                          <span className="px-1 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`flex size-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? "bg-primary-600 text-white"
                              : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    );
                  })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage >= totalPages}
                  className="flex size-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PastRuns;
